import { useEffect } from 'react';
import { useProfile } from '../context/ProfileContext';

/**
 * DragonScaleBackdrop — applies the dragon scale texture as a fixed,
 * full-viewport background via a dedicated <div> appended to the document
 * body. Bypasses the body's own bg-color (which is opaque #000 from CSS)
 * by living in a separate element BEHIND the React app root (z-index: 0)
 * while the React app sits at z-index: 1.
 *
 * Per user direction: "very back background, suggestion of
 * dragons without ever being clear". Opacity intentionally low so cards on
 * top read crisply; only the gaps between cards show the texture.
 *
 * Adaptive Mode: the iron-red dragon scale clashes with
 * the light sand palette, so we disable the backdrop entirely when
 * `profile.adaptiveMode` is true. A sand/cream variant of the scale is
 * backlogged for a future session to evaluate.
 *
 * ─────────────────────────────────────────────────────────────────────
 * 🎛️  ADJUSTABLE OPACITY — change these two numbers to taste:
 * ─────────────────────────────────────────────────────────────────────
 *   OPACITY_DESKTOP — LCD/IPS monitors render dark patterns well; lower works.
 *   OPACITY_MOBILE  — OLED/AMOLED phones paint pure-black as "off" pixels,
 *                     swallowing low-opacity patterns. Bump this higher.
 *
 *   Range: 0.0 (invisible) to 1.0 (opaque). Most tuning lives between 0.10 and 0.30.
 *   BootSplash.jsx has its own opacity setting — search for `opacity: 0.13`.
 */
const OPACITY_DESKTOP = 0.21;
const OPACITY_MOBILE = 0.30;
const MOBILE_BREAKPOINT_PX = 768;

const DragonScaleBackdrop = () => {
  const { profile } = useProfile();
  const adaptiveMode = !!profile?.adaptiveMode;

  useEffect(() => {
    // Adaptive mode: skip the dragon scale entirely. Body bg handled by
    // index.css `body[data-mode="adaptive"]` rule (sand color).
    if (adaptiveMode) {
      const existing = document.getElementById('dragon-scale-backdrop');
      if (existing) existing.remove();
      const root = document.getElementById('root');
      if (root) {
        root.style.position = '';
        root.style.zIndex = '';
        root.style.background = '';
      }
      document.body.style.background = '';
      return undefined;
    }

    // Athlete mode: paint the dragon scale backdrop.
    const existing = document.getElementById('dragon-scale-backdrop');
    if (existing) existing.remove();

    const el = document.createElement('div');
    el.id = 'dragon-scale-backdrop';
    el.setAttribute('aria-hidden', 'true');

    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
    const applyOpacity = (isMobile) => {
      el.style.opacity = String(isMobile ? OPACITY_MOBILE : OPACITY_DESKTOP);
    };

    Object.assign(el.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      zIndex: '0',
      pointerEvents: 'none',
      backgroundImage: 'url(/generated/dragon-scale-texture.png)',
      backgroundRepeat: 'repeat',
      backgroundSize: '600px 600px',
      backgroundAttachment: 'fixed',
    });
    applyOpacity(mq.matches);
    document.body.insertBefore(el, document.body.firstChild);

    // React root sits ABOVE the backdrop.
    const root = document.getElementById('root');
    if (root) {
      root.style.position = 'relative';
      root.style.zIndex = '1';
      root.style.background = 'transparent';
    }
    // Body background: #0a0a0a (near-black) instead of #000. On OLED, pure
    // #000 means pixels are turned OFF entirely, swallowing any low-opacity
    // pattern painted on top. Giving the body a faint baseline of light
    // (#0a0a0a still reads as black) lets the OLED pixels actually render
    // the dragon-scale pattern. Invisible to LCD users, dramatic to OLED.
    document.body.style.background = '#0a0a0a';

    // Re-apply opacity on viewport resize (phone rotate, devtools toggling).
    const onChange = (e) => applyOpacity(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);  // Safari < 14 fallback

    return () => {
      const node = document.getElementById('dragon-scale-backdrop');
      if (node) node.remove();
      if (root) {
        root.style.position = '';
        root.style.zIndex = '';
        root.style.background = '';
      }
      document.body.style.background = '';
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, [adaptiveMode]);
  return null;
};

export default DragonScaleBackdrop;
